import React, { useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useUser } from "@clerk/clerk-react";
import {
  Box, Typography, Paper, Stack, Button, Dialog,
  DialogTitle, DialogContent, DialogActions, TextField, Chip,
  useTheme, IconButton, Tooltip, Avatar, alpha, Divider, Grid, Slide, Autocomplete
} from "@mui/material";
import {
  AddOutlined as AddIcon,
  BusinessOutlined as TheaterIcon,
  LocationOnOutlined as LocationIcon,
  VerifiedOutlined as VerifiedIcon,
  VisibilityOutlined as ViewIcon,
  Sync as SyncIcon,
  Settings as SettingsIcon,
  CheckCircle as ActiveIcon
} from "@mui/icons-material";
import { MaterialReactTable, useMaterialReactTable } from "material-react-table";
import { addNewTheater, getPartnerProfile } from "../../store/slices/partnerSlice";
import { toast } from "react-toastify";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const indianCities = [
  "Mumbai", "Delhi", "Bangalore", "Hyderabad", "Ahmedabad", "Chennai", "Kolkata", "Surat",
  "Pune", "Jaipur", "Lucknow", "Kanpur", "Nagpur", "Indore", "Thane", "Bhopal",
  "Visakhapatnam", "Pimpri-Chinchwad", "Patna", "Vadodara", "Ghaziabad", "Ludhiana",
  "Agra", "Nashik", "Faridabad", "Meerut", "Rajkot", "Kalyan-Dombivli", "Vasai-Virar", "Varanasi"
];

const PartnerTheaters = () => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const mode = theme.palette.mode;
  const { user: clerkUser } = useUser();
  const { partner, loading } = useSelector(s => s.partner);
  const isDark = mode === 'dark';

  const colors = useMemo(() => ({
    primary: "#666CFF",
    success: "#72E128",
    warning: "#FDB528",
    error: "#FF4D49",
    info: "#26C6F9",
    textPrimary: isDark ? "rgba(231, 227, 252, 0.87)" : "rgba(58, 53, 65, 0.87)",
    textSecondary: isDark ? "rgba(231, 227, 252, 0.6)" : "rgba(58, 53, 65, 0.6)",
    background: isDark ? "#282A42" : "#ffffff",
    paper: isDark ? "#30334E" : "#ffffff",
    divider: isDark ? "rgba(231, 227, 252, 0.12)" : "rgba(58, 53, 65, 0.12)",
  }), [isDark]);

  const [addDialog, setAddDialog] = useState(false);
  const activeTheaterId = localStorage.getItem('activeTheaterId');

  const [formData, setFormData] = useState({
    name: '',
    location: '',
    address: { city: '', state: '', pincode: '', line1: '' },
    totalScreens: 1
  });

  const handleOpenAdd = () => {
    const plan = partner?.currentPlan || 'Starter';
    const limit = plan === 'Starter' ? 1 : plan === 'Growth' ? 2 : plan === 'Pro' ? 5 : 999;
    if (partner?.theaters?.length >= limit) {
      return toast.error(`Your ${plan} plan allows up to ${limit} theater(s). Upgrade for more!`);
    }
    setAddDialog(true);
  };

  const handleSubmit = async () => {
    if (!formData.name || !formData.address.city) return toast.error("Missing required fields");
    try {
      const submitData = { ...formData, location: formData.address.city };
      await dispatch(addNewTheater({ userId: clerkUser.id, theaterData: submitData })).unwrap();
      toast.success("Theater added! Awaiting admin verification.");
      setAddDialog(false);
      dispatch(getPartnerProfile(clerkUser.id));
    } catch (err) {
      toast.error(err.message || "Failed to add theater");
    }
  };

  const handleSwitchTheater = (id) => {
    localStorage.setItem('activeTheaterId', id);
    window.location.reload();
    toast.success("Intelligence context switched");
  };

  const theaters = useMemo(() => partner?.theaters || [], [partner]);

  const columns = useMemo(() => [
    {
      accessorKey: 'name',
      header: 'Theater Center',
      Cell: ({ row, renderedCellValue }) => (
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Avatar sx={{ bgcolor: alpha(colors.primary, 0.1), color: colors.primary, borderRadius: '8px', width: 36, height: 36 }}>
            <TheaterIcon sx={{ fontSize: 20 }} />
          </Avatar>
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: '0.875rem', color: colors.textPrimary }}>{renderedCellValue}</Typography>
            <Typography sx={{ fontSize: '0.75rem', color: colors.textSecondary }}>UID: {row.original._id?.slice(-6).toUpperCase()}</Typography>
          </Box>
        </Stack>
      ),
    },
    {
      accessorKey: 'address.city',
      header: 'Location',
      Cell: ({ row }) => (
        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ color: colors.textSecondary }}>
          <LocationIcon sx={{ fontSize: 14 }} />
          <Typography sx={{ fontSize: '0.8rem', fontWeight: 600 }}>{row.original.address?.city || row.original.location}</Typography>
        </Stack>
      ),
    },
    {
      accessorKey: 'screens',
      header: 'Infrastructure',
      Cell: ({ renderedCellValue }) => (
        <Chip
          label={`${renderedCellValue?.length || 0} Screens`}
          size="small"
          variant="outlined"
          sx={{ fontWeight: 700, fontSize: '0.7rem', borderColor: alpha(colors.textSecondary, 0.2) }}
        />
      ),
    },
    {
      accessorKey: 'status.isVerified',
      header: 'Registry Status',
      Cell: ({ renderedCellValue }) => (
        <Chip
          label={renderedCellValue ? 'VERIFIED' : 'PENDING'}
          size="small"
          icon={renderedCellValue ? <VerifiedIcon sx={{ fontSize: '12px !important' }} /> : null}
          sx={{
            height: 22, fontWeight: 900, fontSize: '0.62rem',
            bgcolor: alpha(renderedCellValue ? colors.success : colors.warning, 0.1),
            color: renderedCellValue ? colors.success : colors.warning
          }}
        />
      ),
    },
    {
      accessorKey: 'isActive',
      header: 'Service State',
      Cell: ({ renderedCellValue }) => (
        <Chip
          label={renderedCellValue ? 'OPERATIONAL' : 'OFFLINE'}
          size="small"
          sx={{
            height: 22, fontWeight: 900, fontSize: '0.62rem',
            bgcolor: alpha(renderedCellValue ? colors.info : colors.error, 0.1),
            color: renderedCellValue ? colors.info : colors.error
          }}
        />
      ),
    },
    {
      id: 'actions',
      header: 'Action Hub',
      Cell: ({ row }) => {
        const isActive = activeTheaterId === row.original._id;
        return (
          <Button
            size="small"
            variant={isActive ? "contained" : "outlined"}
            disabled={!row.original.status?.isVerified && theaters.length > 0}
            onClick={() => handleSwitchTheater(row.original._id)}
            startIcon={isActive ? <ActiveIcon /> : <SyncIcon />}
            sx={{
              borderRadius: '8px',
              textTransform: 'none',
              fontWeight: 800,
              fontSize: '0.75rem',
              py: 0.5,
              minWidth: 140,
              ...(isActive ? {
                bgcolor: colors.success,
                '&:hover': { bgcolor: alpha(colors.success, 0.8) }
              } : {
                borderColor: alpha(colors.primary, 0.3),
                color: colors.primary
              })
            }}
          >
            {isActive ? "Active Hub" : (row.original.status?.isVerified ? "Sync Center" : "Wait Verification")}
          </Button>
        );
      },
    },
  ], [colors, activeTheaterId, theaters.length]);

  const table = useMaterialReactTable({
    columns,
    data: theaters,
    enableColumnActions: false,
    enableColumnFilters: false,
    enablePagination: false,
    enableSorting: true,
    enableBottomToolbar: false,
    enableTopToolbar: false,
    muiTablePaperProps: {
      elevation: 0,
      sx: {
        borderRadius: '12px',
        border: `1px solid ${colors.divider}`,
        bgcolor: colors.paper,
      },
    },
    muiTableHeadCellProps: {
      sx: {
        fontWeight: 800,
        fontSize: '0.7rem',
        color: colors.textSecondary,
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        py: 2,
        bgcolor: alpha(colors.textPrimary, 0.02),
      },
    },
    muiTableBodyCellProps: {
      sx: {
        py: 1.5,
        color: colors.textPrimary,
        borderBottom: `1px solid ${colors.divider}`,
      },
    },
  });

  return (
    <Box sx={{ pb: 5 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", mb: 4 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 800, color: colors.textPrimary, letterSpacing: '-0.02em', mb: 0.5 }}>
            Hub Configuration
          </Typography>
          <Typography variant="body2" sx={{ color: colors.textSecondary, fontWeight: 500 }}>
            Manage and orchestrate your cinema network centers from a unified registry
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpenAdd}
          sx={{ borderRadius: '10px', px: 3, py: 1, fontWeight: 700, bgcolor: colors.primary, '&:hover': { bgcolor: alpha(colors.primary, 0.8) } }}>
          Register New Node
        </Button>
      </Box>

      <MaterialReactTable table={table} />

      <Dialog
        open={addDialog}
        onClose={() => setAddDialog(false)}
        maxWidth="sm"
        fullWidth
        TransitionComponent={Transition}
        PaperProps={{
          sx: {
            borderRadius: '28px',
            bgcolor: isDark ? alpha('#0f1014', 0.95) : '#fff',
            backgroundImage: 'none',
            border: `1px solid ${alpha(colors.textPrimary, 0.08)}`,
            backdropFilter: 'blur(30px)',
            boxShadow: isDark ? '0 50px 100px rgba(0,0,0,0.8)' : '0 20px 60px rgba(0,0,0,0.08)'
          }
        }}
      >
        <DialogTitle sx={{ p: 4, pb: 2 }}>
          <Stack direction="row" spacing={2.5} alignItems="center">
            <Avatar sx={{ bgcolor: alpha(colors.primary, 0.1), color: colors.primary, width: 56, height: 56, borderRadius: '18px', border: `1px solid ${alpha(colors.primary, 0.2)}` }}>
              <TheaterIcon sx={{ fontSize: 28 }} />
            </Avatar>
            <Box>
              <Typography sx={{ fontWeight: 950, fontSize: '1.5rem', color: colors.textPrimary, letterSpacing: '-0.03em', lineHeight: 1.2 }}>
                Bootstrap Hub
              </Typography>
              <Typography sx={{ fontSize: '0.7rem', fontWeight: 800, color: colors.primary, letterSpacing: '0.12em', textTransform: 'uppercase', mt: 0.5 }}>
                Infrastructure Node Initialization
              </Typography>
            </Box>
          </Stack>
        </DialogTitle>

        <DialogContent sx={{ p: 4, pt: 1 }}>
          <Typography sx={{ color: colors.textSecondary, mb: 4, fontSize: '0.9rem', fontWeight: 500, lineHeight: 1.6, opacity: 0.8 }}>
            Initialize a new cinema orchestration center within your theater network. All nodes undergo a standard security verification handshake.
          </Typography>

          <Stack spacing={3}>
            <TextField
              fullWidth
              label="Center Designation (Name)"
              placeholder="e.g. District Cineplex X"
              value={formData.name}
              onChange={e => setFormData({ ...formData, name: e.target.value })}
              variant="outlined"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '16px',
                  bgcolor: alpha(colors.textPrimary, 0.02),
                  '& fieldset': { borderColor: alpha(colors.textPrimary, 0.1) },
                },
                '& .MuiInputLabel-root': { fontWeight: 700, fontSize: '0.85rem' }
              }}
            />

            <Grid container spacing={2}>
              <Grid size={{ xs: 6 }}>
                <Autocomplete
                  freeSolo
                  options={indianCities}
                  value={formData.address.city}
                  onChange={(_, newValue) => setFormData({ ...formData, address: { ...formData.address, city: newValue || '' } })}
                  onInputChange={(_, newInputValue) => setFormData({ ...formData, address: { ...formData.address, city: newInputValue } })}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      label="Jurisdiction (City)"
                      variant="outlined"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '16px',
                          bgcolor: alpha(colors.textPrimary, 0.02),
                          '& fieldset': { borderColor: alpha(colors.textPrimary, 0.1) },
                        },
                        '& .MuiInputLabel-root': { fontWeight: 700, fontSize: '0.85rem' }
                      }}
                    />
                  )}
                />
              </Grid>
              <Grid size={{ xs: 6 }}>
                <TextField
                  fullWidth
                  label="State/Region"
                  value={formData.address.state}
                  onChange={e => setFormData({ ...formData, address: { ...formData.address, state: e.target.value } })}
                  variant="outlined"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '16px',
                      bgcolor: alpha(colors.textPrimary, 0.02),
                      '& fieldset': { borderColor: alpha(colors.textPrimary, 0.1) },
                    },
                    '& .MuiInputLabel-root': { fontWeight: 700, fontSize: '0.85rem' }
                  }}
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  label="Physical Vector (Address Line)"
                  value={formData.address.line1}
                  onChange={e => setFormData({ ...formData, address: { ...formData.address, line1: e.target.value } })}
                  variant="outlined"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '16px',
                      bgcolor: alpha(colors.textPrimary, 0.02),
                      '& fieldset': { borderColor: alpha(colors.textPrimary, 0.1) },
                    },
                    '& .MuiInputLabel-root': { fontWeight: 700, fontSize: '0.85rem' }
                  }}
                />
              </Grid>
              <Grid size={{ xs: 6 }}>
                <TextField
                  fullWidth
                  label="Postal Code"
                  value={formData.address.pincode}
                  onChange={e => setFormData({ ...formData, address: { ...formData.address, pincode: e.target.value } })}
                  variant="outlined"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '16px',
                      bgcolor: alpha(colors.textPrimary, 0.02),
                      '& fieldset': { borderColor: alpha(colors.textPrimary, 0.1) },
                    },
                    '& .MuiInputLabel-root': { fontWeight: 700, fontSize: '0.85rem' }
                  }}
                />
              </Grid>
              <Grid size={{ xs: 6 }}>
                <TextField
                  fullWidth
                  label="Initial Screen Capacity"
                  type="number"
                  value={formData.totalScreens}
                  onChange={e => setFormData({ ...formData, totalScreens: parseInt(e.target.value) || 1 })}
                  variant="outlined"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '16px',
                      bgcolor: alpha(colors.textPrimary, 0.02),
                      '& fieldset': { borderColor: alpha(colors.textPrimary, 0.1) },
                    },
                    '& .MuiInputLabel-root': { fontWeight: 700, fontSize: '0.85rem' }
                  }}
                />
              </Grid>
            </Grid>
          </Stack>
        </DialogContent>

        <DialogActions sx={{ p: 4, pt: 1, justifyContent: 'space-between' }}>
          <Button
            onClick={() => setAddDialog(false)}
            sx={{ fontWeight: 800, color: colors.textSecondary, textTransform: 'none', px: 3, borderRadius: '12px' }}
          >
            Abort Initialization
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            sx={{
              borderRadius: '16px',
              px: 5,
              py: 2,
              fontWeight: 900,
              textTransform: 'none',
              bgcolor: colors.primary,
              boxShadow: `0 15px 30px ${alpha(colors.primary, 0.3)}`,
              '&:hover': { bgcolor: alpha(colors.primary, 0.8), transform: 'translateY(-2px)' },
              transition: '0.3s cubic-bezier(0.4, 0, 0.2, 1)'
            }}
          >
            Sync & Register Node
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PartnerTheaters;
